import React, { ReactElement, useState } from 'react'
import styled from 'styled-components'
import { getFont } from '../helpers/dataTransformer'
import { renderHTML } from '../helpers/HTMLRenderer'
import {
  COMPLIMENTARY_GIFT_ITEM_IMAGE_URL,
  getItemColorTheme,
  isGiftVoucher,
  isTESO,
} from '../helpers/itemHelper'
import colors from '../lib/colors'
import theme from '../lib/theme'
import { CurrencyCode } from '../types/API'
import {
  IGiftItemResponse,
  IColorTheme,
  IAddToBasket,
  GiftItemType,
  SelectedPrice,
} from '../types/itemTypes'
import { Font } from '../types/organisation'
import ItemButtons from './buttons/ItemButtons'
import CompactItem from './CompactItem'
import Details from './contents/Details'
import Price from './contents/Price'
import Prices from './contents/Prices'
import Remaining from './contents/Remaining'
import Validity from './contents/Validity'
import useIsNarrow from './helpers/isNarrow'

const EmbedContainer = styled.div`
  &.embedMode {
    padding: 20px 10px 0;
  }
`
const Styles = styled.div`
  min-width: 237px;
  max-width: 237px;
  margin: auto;
  height: 374px;
  box-shadow: 0px 2px 4px ${colors.shadow};
  font-family: ${(props): string => props.theme};
  position: relative;
  background-color: ${colors.white};
  &.embedMode {
    height: auto;
    max-width: 670px;
    display: grid;
    grid-template-columns: 256px 2fr;
    margin: auto;
  }
  &.item-error:after {
    content: ' ';
    position: absolute;
    top: 0;
    right: 0;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 0 20px 20px 0;
    border-color: transparent ${colors.red} transparent transparent;
  }
`
const ContentWrapper = styled.div`
  &.embedMode {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
`
const ImageContainer = styled.div`
  position: relative;
  height: 192px;
  box-shadow: 0px 2px 4px ${colors.shadow};
`
const Image = styled.div`
  height: 100%;
  width: 100%;
  background: url(${(props): string => props.theme});
  background-size: cover;
  background-position: center;
`
const Overlay = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: url(${(props): string => props.theme});
  background-size: cover;
  background-position: center;
`
const Contents = styled.div`
  height: 144px;
  position: relative;
  display: flex;
  flex-direction: column;
  color: ${(props): string => props.theme.colourBodyText};
`
const StartInfo = styled.div`
  font-size: 14px;
  margin-bottom: 4px;
`
const Title = styled.div`
  font-size: 15px;
  color: ${(props): string => props.theme.colourHeaderText};
  margin-bottom: 2px;
`
const Description = styled.div`
  font-size: 13px;
  word-break: break-word;
  height: 68px;
  p {
    margin: 0;
  }
`
const Scroll = styled.div`
  overflow-y: auto;
  ${theme.scrollbar};
  padding: 10px 9px 0;
  height: 115px;
`
const Padded = styled.div`
  margin: auto 9px 0;
`
interface IItemProps extends IAddToBasket {
  giftItem: IGiftItemResponse
  currency: CurrencyCode
  organisationColors?: IColorTheme
  embedMode?: boolean
  font?: Font
  setDetails?: boolean
  compact?: boolean
}
export default function Item({
  giftItem,
  currency,
  addToBasket,
  organisationColors,
  embedMode,
  font,
  setDetails = false,
  compact,
}: IItemProps): ReactElement {
  const [showDetails, setShowDetails] = useState(setDetails)
  const [showPrices, setShowPrices] = useState(false)

  const {
    imageUrl,
    showOverlay,
    overlayImageUrl,
    name,
    value,
    quantity,
    quantitySold,
    showRemaining,
    expiryDate,
    startInfo,
    startDate,
    originalPrice,
    type,
    description,
    maxIndividuals,
    prices,
  } = giftItem

  const [individuals, setIndividuals] = useState(
    maxIndividuals && maxIndividuals > 1 ? 2 : 1,
  )

  const soldOut = (quantitySold && quantity && quantitySold >= quantity) || false

  const colorTheme = getItemColorTheme(organisationColors)

  const isItemTESO = isTESO(giftItem)
  const isItemGiftVoucher = isGiftVoucher(giftItem)

  const isError = isItemTESO && isItemGiftVoucher

  const isNarrow = useIsNarrow()
  const embedClass = embedMode && !isNarrow ? 'embedMode' : ''
  const errorClass = isError ? 'item-error' : ''

  const isComplimentary = type === GiftItemType.Complimentary

  const image = isComplimentary ? COMPLIMENTARY_GIFT_ITEM_IMAGE_URL : imageUrl

  const initSelected = prices && prices.length > 0
    ? prices.map((price) => ({ id: price.id, value: price.value, count: 0 }))
    : []
  const [pricesSelected, setPricesSelected] = useState<SelectedPrice[]>(initSelected)

  const buyPrices = (): void => {
    if (!addToBasket) return

    pricesSelected.forEach((price) => {
      if (price.count === 0) return

      const priceGiftItem = {
        ...giftItem,
        originalId: giftItem.id,
        id: price.id,
        value: price.value,
      }

      addToBasket(
        priceGiftItem,
        price.value ? parseFloat(price.value) : 0,
        price.count,
        1,
      )
    })
  }

  if (compact) {
    return (
      <CompactItem
        giftItem={giftItem}
        currency={currency}
        individuals={individuals}
      />
    )
  }

  return (
    <EmbedContainer className={embedClass}>
      <Styles className={`${embedClass} ${errorClass}`} theme={getFont(font)}>
        <ImageContainer>
          <Image theme={image} />
          {!isComplimentary && showOverlay && (
            <Overlay theme={overlayImageUrl} />
          )}
        </ImageContainer>
        <ContentWrapper className={embedClass}>
          <Contents theme={colorTheme}>
            <Scroll>
              {startInfo && <StartInfo>{startInfo}</StartInfo>}
              <Title theme={colorTheme}>{name || ''}</Title>
              <Validity startDate={startDate} expiryDate={expiryDate} />
              {description && (
                <Description theme={colorTheme}>
                  {renderHTML(description)}
                </Description>
              )}
            </Scroll>
            <Padded>
              <Price
                value={value || ''}
                originalPrice={originalPrice}
                currencyCode={currency}
                maxIndividuals={maxIndividuals}
                individuals={individuals}
                setIndividuals={setIndividuals}
                colorTheme={colorTheme}
                prices={prices}
              />
              <Remaining
                showRemaining={showRemaining}
                quantity={quantity}
                quantitySold={quantitySold}
                soldOut={soldOut}
              />
            </Padded>
          </Contents>
          <ItemButtons
            giftItem={giftItem}
            currency={currency}
            showDetails={showDetails}
            setShowDetails={setShowDetails}
            addToBasket={addToBasket}
            colorTheme={colorTheme}
            individuals={individuals}
            soldOut={soldOut}
            showQuantity={isItemTESO && !isItemGiftVoucher}
            showPrices={showPrices}
            setShowPrices={setShowPrices}
            buyPrices={buyPrices}
            resetPricesSelected={(): void => setPricesSelected(initSelected)}
            pricesSelected={pricesSelected}
          />
        </ContentWrapper>
        <Details
          embedMode={embedMode || false}
          giftItem={giftItem}
          showDetails={showDetails}
          setShowDetails={setShowDetails}
        />
        {prices && prices.length > 0 && addToBasket && (
          <Prices
            embedMode={embedMode || false}
            giftItem={giftItem}
            colorTheme={colorTheme}
            showPrices={showPrices}
            setShowPrices={setShowPrices}
            currency={currency}
            pricesSelected={pricesSelected}
            setPricesSelected={setPricesSelected}
          />
        )}
      </Styles>
    </EmbedContainer>
  )
}
